import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import {
	useOptionalTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import fontStylesheet from './styles/font-styles.css?url'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { HoneypotInputProps } from 'remix-utils/honeypot/server'
import { PartytownGoogleTagManager } from './components/partytown-gtm.tsx'
import { Partytown } from '@qwik.dev/partytown/react'
import { maybeProxyRequest, partytownAtomicHeaders } from './utils/partytown.ts'
import { GtmProvider } from './utils/gtm-context.tsx'
import { COMPANY_NAME, LOCATION_CITY_STATE, SERVICE_COUNTIES } from './utils/constants.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{rel: 'preconnect', href: 'https://cdn.sanity.io'},
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'preload', href: fontStylesheet, as: 'style' },
		{ rel: 'stylesheet', href: fontStylesheet },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? COMPANY_NAME : `Error | ${COMPANY_NAME}` },
		{ name: 'description', content: `${COMPANY_NAME} is a garage door repair company based in ${LOCATION_CITY_STATE}, serving ${SERVICE_COUNTIES}. We provide garage door repair, installation, and maintenance services for residential and commercial properties.` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			gtmContainerId: process.env.GOOGLE_TAG_MANAGER_CONTAINER_ID || '',
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				partytownAtomicHeaders()
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	gtmContainerId
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
	gtmContainerId?: string
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<PartytownGoogleTagManager gtmContainerId={gtmContainerId} />
				<Partytown
				nonce={nonce}
				forward={['dataLayer.push']}
				resolveUrl={maybeProxyRequest}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV} gtmContainerId={data?.gtmContainerId}>
			{children}
		</Document>
	)
}

export type OutletContext = {
	requestInfo: {
		hints: typeof getHints,
		origin: string,
		path: string,
		userPrefs: {
			theme: Theme | null,
		},
	},
	ENV: ReturnType<typeof getEnv>,
	toast: {
		id: string;
		type: "error" | "message" | "success";
		description: string;
		title?: string | undefined;
	} | null,
	honeyProps: HoneypotInputProps,
	gtmContainerId: string,
}

function App() {
	const data = useLoaderData<typeof loader>()
	return (
		<Outlet context={{ ...data }} />
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<GtmProvider>
				<App />
			</GtmProvider>
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
